/**
 * Chart export utilities for CSV, SVG, and PNG formats
 * Provides comprehensive export functionality for chart visualizations
 */

import { saveAs } from "file-saver";
import Papa from "papaparse";
import type React from "react";
import type { BaseDataPoint } from "./charts";

export type ExportType = "csv" | "svg" | "png";

export interface ChartExportMetadata {
  xLabel?: string;
  yLabel?: string;
  seriesLabels?: string[];
  filename?: string;
  timestamp?: boolean;
}

/** SVG presentation attributes that may contain CSS custom properties */
const SVG_PRESENTATION_ATTRS = ["stroke", "fill", "color", "stop-color", "flood-color", "lighting-color"] as const;

/** CSS variable pattern: var( with optional whitespace */
const CSS_VAR_PATTERN = /var\s*\(/;

/**
 * Resolves CSS custom properties (e.g. `var(--color-brand-primary)`) in a cloned SVG
 * by reading computed styles from the original DOM-attached SVG.
 *
 * When an SVG is cloned and serialized for export (PNG/SVG), it loses access to the
 * page's CSS context, so any `var(--*)` values become empty/transparent. This function
 * walks both trees in parallel and replaces unresolved variables with their computed values.
 *
 * @param original - The original SVG element still attached to the DOM
 * @param clone - The cloned SVG element to be exported
 */
export function resolveCSSVariables(original: SVGElement, clone: SVGElement): void {
  const originalElements = [original, ...Array.from(original.querySelectorAll("*"))] as Element[];
  const cloneElements = [clone, ...Array.from(clone.querySelectorAll("*"))] as Element[];

  // Both trees have identical structure since clone = cloneNode(true)
  const count = Math.min(originalElements.length, cloneElements.length);

  for (let i = 0; i < count; i++) {
    const origEl = originalElements[i];
    const cloneEl = cloneElements[i];

    // Skip non-SVG/HTML elements
    if (!(origEl instanceof Element) || !(cloneEl instanceof Element)) continue;

    const computed = window.getComputedStyle(origEl);

    // Check SVG presentation attributes for CSS variables
    for (const attr of SVG_PRESENTATION_ATTRS) {
      const attrValue = cloneEl.getAttribute(attr);
      if (attrValue && CSS_VAR_PATTERN.test(attrValue)) {
        const resolved = computed.getPropertyValue(attr);
        if (resolved) {
          cloneEl.setAttribute(attr, resolved.trim());
        }
      }
    }

    // Check inline style properties for CSS variables
    if (cloneEl instanceof HTMLElement || cloneEl instanceof SVGElement) {
      for (const attr of SVG_PRESENTATION_ATTRS) {
        // getPropertyValue/setProperty accept hyphenated CSS property names
        const styleProp = attr;
        const styleValue = cloneEl.style.getPropertyValue(styleProp);
        if (styleValue && CSS_VAR_PATTERN.test(styleValue)) {
          const resolved = computed.getPropertyValue(styleProp);
          if (resolved) {
            cloneEl.style.setProperty(styleProp, resolved.trim());
          }
        }
      }
    }
  }
}

/**
 * Generates a filename with optional timestamp.
 *
 * @param baseFilename - Base name for the file
 * @param extension - File extension (without dot)
 * @param includeTimestamp - Whether to append timestamp (default: true)
 * @returns Complete filename with extension
 */
function generateFilename(baseFilename: string, extension: string, includeTimestamp: boolean = true): string {
  const base = baseFilename || "chart";
  if (includeTimestamp) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    return `${base}-${timestamp}.${extension}`;
  }
  return `${base}.${extension}`;
}

/**
 * Main export function that handles all chart export types.
 * Supports CSV, SVG, and PNG formats with automatic filename generation.
 *
 * @param type - Export format: "csv", "svg", or "png"
 * @param datasets - Chart data as single array or array of arrays for multi-series
 * @param metadata - Export metadata including labels and filename options
 * @param svgRef - Reference to the chart's SVG element
 * @returns Promise that resolves when export is complete
 *
 * @example
 * ```typescript
 * // Export single series as CSV
 * await exportChart("csv", data, {
 *   xLabel: "Date",
 *   yLabel: "Value",
 *   filename: "sales-data"
 * }, svgRef);
 *
 * // Export multi-series chart as PNG
 * await exportChart("png", [series1, series2], {
 *   seriesLabels: ["Revenue", "Profit"],
 *   timestamp: false
 * }, svgRef);
 * ```
 */
export async function exportChart(
  type: ExportType,
  datasets: BaseDataPoint[][] | BaseDataPoint[],
  metadata: ChartExportMetadata = {},
  svgRef: React.RefObject<SVGSVGElement | null>
): Promise<void> {
  // Normalize datasets to always be an array of arrays
  const normalizedDatasets = Array.isArray(datasets[0])
    ? (datasets as BaseDataPoint[][])
    : [datasets as BaseDataPoint[]];

  try {
    switch (type) {
      case "csv":
        await exportAsCSV(normalizedDatasets, metadata);
        break;
      case "svg":
        await exportAsSVG(svgRef, metadata);
        break;
      case "png":
        await exportAsPNG(svgRef, metadata);
        break;
      default:
        throw new Error(`Unsupported export type: ${type}`);
    }
  } catch (error) {
    console.error(`Export failed:`, error);
    throw new Error(`Failed to export chart as ${type}: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Export chart data as CSV
 */
async function exportAsCSV(datasets: BaseDataPoint[][], metadata: ChartExportMetadata): Promise<void> {
  try {
    // Create a map of all unique dates across all datasets
    const dateMap = new Map<number, Date>();
    datasets.forEach((dataset) => {
      dataset.forEach((point) => {
        const timestamp = point.xValue.getTime();
        if (!dateMap.has(timestamp)) {
          dateMap.set(timestamp, point.xValue);
        }
      });
    });

    // Sort dates chronologically
    const sortedDates = Array.from(dateMap.values()).sort((a, b) => a.getTime() - b.getTime());

    // Build rows with all series data
    const rows = sortedDates.map((date) => {
      const row: Record<string, string | number> = {
        [metadata.xLabel || "Date"]: date.toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      // Add value from each dataset
      datasets.forEach((dataset, index) => {
        const label = metadata.seriesLabels?.[index] || `Series ${index + 1}`;
        const point = dataset.find((p) => p.xValue.getTime() === date.getTime());
        row[label] = point ? point.yValue : "";

        // Add category if present
        if (point?.category) {
          row[`${label} Category`] = point.category;
        }
      });

      return row;
    });

    // Generate CSV using papaparse
    const csv = Papa.unparse(rows, {
      header: true,
      skipEmptyLines: true,
    });

    // Create and download the file
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const filename = generateFilename(metadata.filename || "chart-data", "csv", metadata.timestamp !== false);
    saveAs(blob, filename);
  } catch (error) {
    throw new Error(`CSV export failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Export chart as SVG
 */
async function exportAsSVG(
  svgRef: React.RefObject<SVGSVGElement | null>,
  metadata: ChartExportMetadata
): Promise<void> {
  if (!svgRef.current) {
    throw new Error("SVG element not found");
  }

  try {
    const svgElement = svgRef.current;

    // Clone the SVG to avoid modifying the original
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Resolve CSS custom properties before serialization
    resolveCSSVariables(svgElement, svgClone);

    // Add background if not present
    const hasBackground = svgClone.querySelector("rect[data-background]");
    if (!hasBackground) {
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("width", svgClone.getAttribute("width") || "100%");
      rect.setAttribute("height", svgClone.getAttribute("height") || "100%");
      rect.setAttribute("fill", "white");
      rect.setAttribute("data-background", "true");
      svgClone.insertBefore(rect, svgClone.firstChild);
    }

    // Serialize the SVG
    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(svgClone);

    // Add XML declaration and namespace if not present
    if (!svgString.includes("<?xml")) {
      svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;
    }

    // Ensure SVG namespace is present
    if (!svgString.includes("xmlns=")) {
      svgString = svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    // Create and download the file
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const filename = generateFilename(metadata.filename || "chart", "svg", metadata.timestamp !== false);
    saveAs(blob, filename);
  } catch (error) {
    throw new Error(`SVG export failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Export chart as PNG with high resolution
 */
async function exportAsPNG(
  svgRef: React.RefObject<SVGSVGElement | null>,
  metadata: ChartExportMetadata,
  scale: number = 2 // Default to 2x resolution for retina displays
): Promise<void> {
  if (!svgRef.current) {
    throw new Error("SVG element not found");
  }

  let objectUrl: string = "";

  try {
    const svgElement = svgRef.current;
    const bbox = svgElement.getBoundingClientRect();

    // Clone and prepare SVG
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Resolve CSS custom properties before serialization
    resolveCSSVariables(svgElement, svgClone);

    // Set explicit dimensions
    svgClone.setAttribute("width", String(bbox.width));
    svgClone.setAttribute("height", String(bbox.height));

    // Remove any transform that might affect rendering
    svgClone.removeAttribute("transform");

    // Ensure all text elements have explicit font settings for consistent rendering
    const textElements = svgClone.getElementsByTagName("text");
    Array.from(textElements).forEach((text) => {
      if (!text.style.fontFamily) {
        text.style.fontFamily = "system-ui, -apple-system, sans-serif";
      }
      if (!text.style.fontSize) {
        text.style.fontSize = "12px";
      }
    });

    // Add white background
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("width", String(bbox.width));
    rect.setAttribute("height", String(bbox.height));
    rect.setAttribute("fill", "white");
    svgClone.insertBefore(rect, svgClone.firstChild);

    // Convert SVG to blob
    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(svgClone);

    // Add namespace
    if (!svgString.includes("xmlns=")) {
      svgString = svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    objectUrl = URL.createObjectURL(svgBlob);

    // Convert to PNG using canvas
    return new Promise((resolve, reject) => {
      const image = new Image();

      // Set up timeout to prevent hanging
      const timeout = setTimeout(() => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        reject(new Error("Image loading timeout"));
      }, 10000);

      image.onload = () => {
        clearTimeout(timeout);

        try {
          // Create canvas with scaling for high resolution
          const canvas = document.createElement("canvas");
          canvas.width = bbox.width * scale;
          canvas.height = bbox.height * scale;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            throw new Error("Canvas context not available");
          }

          // Enable image smoothing for better quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";

          // Scale the context for high resolution
          ctx.scale(scale, scale);

          // Fill with white background
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, bbox.width, bbox.height);

          // Draw the image
          ctx.drawImage(image, 0, 0, bbox.width, bbox.height);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const filename = generateFilename(metadata.filename || "chart", "png", metadata.timestamp !== false);
                saveAs(blob, filename);
                resolve();
              } else {
                reject(new Error("Failed to create PNG blob"));
              }

              // Clean up
              if (objectUrl) URL.revokeObjectURL(objectUrl);
            },
            "image/png",
            1.0 // Maximum quality
          );
        } catch (error) {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      image.onerror = () => {
        clearTimeout(timeout);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load SVG as image"));
      };

      // Load the image
      image.src = objectUrl;
    });
  } catch (error) {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    throw new Error(`PNG export failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Utility to check if export is supported in the current environment
 */
export function isExportSupported(type: ExportType): boolean {
  if (typeof window === "undefined") return false;

  switch (type) {
    case "csv":
      return true; // Always supported
    case "svg":
      return typeof XMLSerializer !== "undefined";
    case "png":
      return typeof HTMLCanvasElement !== "undefined" && typeof HTMLCanvasElement.prototype.toBlob === "function";
    default:
      return false;
  }
}

/**
 * Get human-readable export format name
 */
export function getExportFormatName(type: ExportType): string {
  const formats: Record<ExportType, string> = {
    csv: "CSV Spreadsheet",
    svg: "SVG Vector Image",
    png: "PNG Image",
  };
  return formats[type] || "Unknown Format";
}
